// modules/docs/annotations/components/NoteCard.tsx

import { formatDate } from "@core/utils/date";
import { Button } from "@core/components";
import { icons } from "@core/assets";
import type { BackendAnnotationMeta } from "@modules/docs/annotations/types/TBackendAnnotation";
import { useRemoveAnnotationNote } from "@modules/docs/annotations/hooks";

type NoteCardProps = {
  note: BackendAnnotationMeta["notes"][number];
  annotationId: string;
  canDelete?: boolean;
};

function NoteCard({ note, annotationId, canDelete = true }: NoteCardProps) {
  const removeNoteMutation = useRemoveAnnotationNote(annotationId, note.id);

  const handleRemoveNote = () => {
    removeNoteMutation.mutate();
  };

  const authorName =
    [note.last_name, note.first_name].filter(Boolean).join(" ") ||
    "Unknown user";

  const isInactive = note.licence === "inactive";

  return (
    <div className="w-full min-h-[58px] rounded-[4px] py-2 bg-secondary-100 flex items-center justify-between px-3">
      <div className="flex flex-col gap-3">
        <h5 className="text-secondary-400 capitalize flex items-center gap-2">
          By{" "}
          <span className="text-secondary-500 font-medium">{authorName}</span>
          {isInactive && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-warning-100 text-warning-600  tracking-wide">
              (Inactive)
            </span>
          )}{" "}
          {formatDate(note.created_at)}
        </h5>
        <h5 className="capitalize font-medium break-words">{note.content}</h5>
      </div>
      {canDelete && (
        <Button
          variant="ghost"
          className="w-[3rem]"
          leftIcon={icons["trash-2"]}
          iconStrokeWidth={1.6}
          onClick={handleRemoveNote}
          isLoading={removeNoteMutation.isPending}
          disabled={removeNoteMutation.isPending}
        />
      )}
    </div>
  );
}

export default NoteCard;
